"use client";

import { cn } from "@gettactic/helpers/src/css/cn";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import {
  AVATAR_SIZES as USER_PHOTO_SIZES,
  UserAvatar as UserPhotoCard
} from "@cmp-src/components/UserAvatar/UserAvatar";
import { api } from "@/lib/api/api";

type Props = {
  dates: { from?: Date; to?: Date } | undefined;
};

export function OutOfOfficeOverlapWarning({ dates }: Props) {
  const [collapsed, setCollapsed] = useState(true);
  const formatted = useMemo(() => {
    if (!dates?.from) return null;
    return {
      start: format(dates.from, "yyyy-MM-dd"),
      end: format(dates.to ?? dates.from, "yyyy-MM-dd")
    };
  }, [dates?.from, dates?.to]);
  const overlapQuery = useQuery({
    queryKey: ["leaveOverlap", formatted],
    enabled: !!formatted,
    queryFn: async () => {
      if (!formatted) return [];
      const res = await api.client.leave.conflicts(formatted);
      return res.result ? res.result.elements : [];
    }
  });

  if (
    !formatted ||
    overlapQuery.isLoading ||
    !overlapQuery.isSuccess ||
    !overlapQuery.data ||
    overlapQuery.data.length === 0
  ) {
    return null;
  }
  const usersAll = overlapQuery.data.map((x) => x.user) ?? [];
  // remove duplicates
  const users = usersAll.filter(
    (v, i, a) => a.findIndex((t) => t.id === v.id) === i
  );
  const max = 6;
  const len = users.length;
  return (
    <div>
      <div className="flex items-center space-x-2">
        <ExclamationCircleIcon
          className="h-10 w-10 text-primary"
          aria-hidden="true"
        />
        <span className="leading-normal text-neutral-500">
          The following team members have approved time off on one or more of
          the selected days
        </span>
      </div>
      <div
        className={cn(
          "mb-5 ml-8 flex",
          collapsed ? "-space-x-4" : "flex-wrap space-x-0"
        )}
      >
        {users.slice(0, collapsed ? max : len).map((user) => (
          <div
            title={user.name}
            key={user.id}
            className="h-8 w-8 rounded-full border-2 border-white dark:border-neutral-800 "
          >
            <UserPhotoCard
              size={USER_PHOTO_SIZES.ROUNDED_FULL}
              profile_photo_url={user.profile_photo_url}
              name={user.name}
            />
          </div>
        ))}
        {len > max ? (
          <a
            onClick={() => setCollapsed(!collapsed)}
            className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-neutral-700 text-xs font-medium text-white hover:bg-neutral-600 dark:border-neutral-800"
            href="#"
          >
            {collapsed ? (
              <>+{len - max}</>
            ) : (
              <ChevronLeftIcon className="h-4 w-4" />
            )}
          </a>
        ) : null}
      </div>
    </div>
  );
}
